import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import notify from 'components/atoms/Notify'
import apiPost from 'lib/network/apiPost'
import apiGet from 'lib/network/apiGet'
import Selection from 'components/molecules/Selection'
import apiPut from 'lib/network/apiPut'
import { CameraIcon } from '@heroicons/react/solid'
import { useSelector } from 'react-redux'
import { get } from 'lodash'
import apiDelete from 'lib/network/apiDelete'

const AddBlackList = ({ onSuccess, close, id = null }) => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'
  const [userOptions, setUserOptions] = useState([])
  const [selectedFraudUser, setSelectedFraudUser] = useState('')
  const userFromStore = useSelector(state => get(state, 'user.user'))
  // eslint-disable-next-line
  const hasAdminAccess = userFromStore?.roles?.some(role => role.type === 'admin')
  const [previews, setPreviews] = useState({})
  const [files, setFiles] = useState({})
  const profileRef = useRef(null)
  const [data, setFormData] = useState({
    name: '',
    civilId: '',
    notes: [],
    phone: '',
    note: '',
    noteAr: '',
  })
  const [showAddNote, setShowAddNote] = useState(false)
  const [newNote, setNewNote] = useState('')
  const [newNoteAr, setNewNoteAr] = useState('')
  const shouldDisableFields = id && !hasAdminAccess

  const showPreview = (key, file) => {
    const preview = URL.createObjectURL(file)
    setPreviews({ ...previews, [key]: preview })
  }

  useEffect(() => {
    if (hasAdminAccess) {
      fetchUsers()
    }
  }, [hasAdminAccess])

  const fetchUsers = async (searchQuery = '') => {
    const response = await apiGet('users', { query: searchQuery })

    if (response?.data) {
      const users = response.data.user.map(user => ({
        id: user._id,
        name: user.fullName || user.email || user.phone,
      }))
      setUserOptions(users)
    } else {
      setUserOptions([])
    }
  }

  useEffect(() => {
    if (id) {
      fetchFraudUserData()
    }
  }, [id])

  const fetchFraudUserData = async () => {
    const response = await apiGet(`fraud-users/${id}`)
    if (response.success) {
      const data = response.data
      setFormData({
        name: data.name || '',
        civilId: data.civilId || '',
        notes: data.note || [],
        phone: data.phone || '',
        note: '',
        noteAr: '',
      })
      setSelectedFraudUser(data?.user?._id || '')

      if (data.image) {
        setPreviews({ image: data.image })
      }
    } else {
      notify.error(t('blacklistModal.messages.fetchDataFailed'))
    }
  }

  const handleChange = e => {
    const { name, value } = e.target

    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }))
  }

  const handleNoteChange = (index, key, value) => {
    setFormData(prevData => ({
      ...prevData,
      notes: prevData.notes.map((note, i) => (i === index ? { ...note, [key]: value } : note)),
    }))
  }

  const handleDeleteNote = async (index, noteId) => {
    if (noteId) {
      try {
        const response = await apiDelete(`fraud-users/${id}/delete-note/${noteId}`)
        if (response.success) {
          notify.success(t('blacklistModal.messages.noteDeletedSuccess'))
          await fetchFraudUserData()
        } else {
          notify.error(response.message || t('blacklistModal.messages.noteDeleteFailed'))
        }
      } catch (error) {
        notify.error(t('blacklistModal.messages.noteDeleteFailed'))
      }
    } else {
      setFormData(prevData => ({
        ...prevData,
        notes: prevData.notes.filter((_, i) => i !== index),
      }))
    }
  }

  const handleAddNewNote = async () => {
    // eslint-disable-next-line
    if (!newNote.trim() && !newNoteAr.trim()) {
      notify.error(t('blacklistModal.messages.pleaseEnterNote'))
      return
    }

    try {
      const response = await apiPost(`fraud-users/${id}/add-note`, {
        note: newNote,
        noteAr: newNoteAr,
      })
      if (response.success) {
        notify.success(t('blacklistModal.messages.noteAddedSuccess'))
        setNewNote('')
        setNewNoteAr('')
        setShowAddNote(false)
        await fetchFraudUserData()
      } else {
        notify.error(response.message || t('blacklistModal.messages.noteAddFailed'))
      }
    } catch (error) {
      notify.error(t('blacklistModal.messages.noteAddFailed'))
    }
  }

  const handleSaveFraudUser = async () => {
    const formDataObj = new FormData()

    // eslint-disable-next-line
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
        if (key === 'notes') {
          // eslint-disable-next-line
          if (id && Array.isArray(data[key]) && data[key].length > 0) {
            formDataObj.append('notes', JSON.stringify(data[key]))
          }
        } else if (key === 'note' || key === 'noteAr') {
          // eslint-disable-next-line
          if (!id && data[key].trim()) formDataObj.append(key, data[key])
        } else formDataObj.append(key, data[key])
      }
    })
    if (selectedFraudUser) formDataObj.append('userId', selectedFraudUser)
    if (files.image) formDataObj.append('image', files.image)

    const endpoint = id ? `fraud-users/${id}` : 'fraud-users'
    const apiMethod = id ? apiPut : apiPost

    try {
      const response = await apiMethod(endpoint, formDataObj)

      if (response.success) {
        const message = id ? t('blacklistModal.messages.fraudUserUpdatedSuccess') : t('blacklistModal.messages.fraudUserAddedSuccess')
        notify.success(message)
        onSuccess(response.data._id)
      } else {
        notify.error(response.message || t('blacklistModal.messages.fraudUserSaveFailed'), null, response.err)
      }
    } catch (error) {
      notify.error(t('blacklistModal.messages.fraudUserSaveFailed'))
    }
  }

  return (
    <div>
      {(hasAdminAccess || !id) && (
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-center sm:pt-5">
          <label htmlFor="photo" className="block text-sm font-medium text-gray-700">
            {t('blacklistModal.form.photo')}
          </label>
          <div className="mt-1 sm:mt-0 sm:col-span-2">
            <div className="flex pb-4 items-center">
              <div className="bg-gray relative h-[200px] w-[150px] border mr-4 border-dashed border-gray-300 rounded-md">
                <input
                  id="image"
                  ref={profileRef}
                  name="image"
                  className="hidden"
                  accept=".jpg,.jpeg,.png"
                  type="file"
                  onChange={event => {
                    showPreview('image', event.target.files[0])
                    setFiles({ ...files, image: event.target.files[0] })
                  }}
                  disabled={shouldDisableFields}
                />
                {previews.image ? (
                  <span onClick={() => !shouldDisableFields && profileRef.current.click()}>
                    <img src={previews.image} alt="" className={`bg-cover h-full w-[150px] rounded ${!shouldDisableFields ? 'cursor-pointer' : ''}`} />
                  </span>
                ) : (
                  <div
                    onClick={() => !shouldDisableFields && profileRef.current.click()}
                    className={`bg-white flex p-2 items-center justify-center bg-contain bg-no-repeat bg-center border-gray-300 rounded-md h-full w-full ${
                      !shouldDisableFields ? 'cursor-pointer' : ''
                    }`}
                  >
                    <CameraIcon className="text-gray-400 h-8 w-8" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {hasAdminAccess && (
        <div className="mb-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
          <label htmlFor="name" className="block text-sm font-normal text-gray-700 mb-1">
            {t('blacklistModal.form.users')}
          </label>
          <div className="mt-1 sm:mt-0 sm:col-span-2">
            <Selection
              onChange={value => {
                if (!shouldDisableFields && value && value.id) {
                  setSelectedFraudUser(prevValue => (prevValue === value.id ? '' : value.id))
                }
              }}
              title={t('blacklistModal.form.selectFraudUser')}
              list={userOptions}
              value={selectedFraudUser}
              showSearch
              onSearch={searchQuery => fetchUsers(searchQuery)}
              className="block w-full shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
              disabled={shouldDisableFields}
            />
          </div>
        </div>
      )}

      <div className="mb-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
        <label htmlFor="name" className="block text-sm font-normal text-gray-700 mb-1">
          {t('blacklistModal.form.fraudName')}
        </label>
        <div className="mt-1 sm:mt-0 sm:col-span-2">
          <input
            type="text"
            name="name"
            id="name"
            value={data.name}
            onChange={handleChange}
            disabled={shouldDisableFields}
            className="block w-full shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
          />
        </div>
      </div>

      <div className="mb-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
        <label htmlFor="phone" className="block text-sm font-normal text-gray-700 mb-1">
          {t('blacklistModal.form.phoneNumber')}
        </label>
        <div className="mt-1 sm:mt-0 sm:col-span-2">
          <input
            type="tel"
            name="phone"
            id="phone"
            value={data.phone}
            onChange={handleChange}
            disabled={shouldDisableFields}
            className="block w-full shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
          />
        </div>
      </div>

      <div className="mb-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
        <label htmlFor="civilId" className="block text-sm font-normal text-gray-700 mb-1">
          {t('blacklistModal.form.fraudCivilId')}
        </label>
        <div className="mt-1 sm:mt-0 sm:col-span-2">
          <input
            type="number"
            name="civilId"
            id="civilId"
            value={data.civilId}
            onChange={handleChange}
            disabled={shouldDisableFields}
            className="block w-full shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
          />
        </div>
      </div>

      {!id && (
        <div className="mb-6">
          <label className="block text-sm font-normal text-gray-700 mb-1">{t('blacklistModal.form.notes')}</label>
          <div className="mb-4">
            <label className="block text-sm font-normal text-gray-700 mb-1">{t('blacklistModal.form.note')}</label>
            <textarea
              name="note"
              value={data.note}
              onChange={handleChange}
              disabled={shouldDisableFields}
              className="w-full border border-gray-300 rounded-md p-2 focus:ring-dark-500 focus:border-dark-500 mb-2"
              rows="3"
              placeholder={t('blacklistModal.form.addNotePlaceholder')}
            />
            <label className="block text-sm font-normal text-gray-700 mb-1">{t('blacklistModal.form.noteAr')}</label>
            <textarea
              name="noteAr"
              value={data.noteAr}
              onChange={handleChange}
              disabled={shouldDisableFields}
              dir="rtl"
              className="w-full border border-gray-300 rounded-md p-2 focus:ring-dark-500 focus:border-dark-500"
              rows="3"
              placeholder={t('blacklistModal.form.addNotePlaceholderAr')}
            />
          </div>
        </div>
      )}

      {id && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="notes" className="block text-sm font-normal text-gray-700">
              {t('blacklistModal.form.notes')}
            </label>
            {!shouldDisableFields && (
              <button
                type="button"
                onClick={() => setShowAddNote(true)}
                className="flex items-center justify-center w-6 h-6 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                title={t('blacklistModal.buttons.addNote')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            )}
          </div>

          {data.notes && data.notes.length > 0 && (
            <>
              {data.notes.map((noteItem, index) => (
                <div key={noteItem._id || index} className="mb-4">
                  <label className="block text-sm font-normal text-gray-700 mb-1">{t('blacklistModal.form.note')}</label>
                  <textarea
                    value={noteItem.note || ''}
                    onChange={e => handleNoteChange(index, 'note', e.target.value)}
                    disabled={shouldDisableFields}
                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-dark-500 focus:border-dark-500 mb-2"
                    rows="2"
                  />
                  <label className="block text-sm font-normal text-gray-700 mb-1">{t('blacklistModal.form.noteAr')}</label>
                  <textarea
                    value={noteItem.noteAr || ''}
                    onChange={e => handleNoteChange(index, 'noteAr', e.target.value)}
                    disabled={shouldDisableFields}
                    dir="rtl"
                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-dark-500 focus:border-dark-500"
                    rows="2"
                  />
                  {!shouldDisableFields && (
                    <button
                      type="button"
                      onClick={() => handleDeleteNote(index, noteItem._id)}
                      className="mt-1 flex items-center justify-center w-6 h-6 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                      title={t('blacklistModal.buttons.deleteNote')}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </>
          )}

          {showAddNote && !shouldDisableFields && (
            <div className="mb-4">
              <label className="block text-sm font-normal text-gray-700 mb-1">{t('blacklistModal.form.note')}</label>
              <textarea
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-dark-500 focus:border-dark-500 mb-2"
                rows="2"
                placeholder={t('blacklistModal.form.addNewNotePlaceholderEn')}
              />
              <label className="block text-sm font-normal text-gray-700 mb-1">{t('blacklistModal.form.noteAr')}</label>
              <textarea
                value={newNoteAr}
                onChange={e => setNewNoteAr(e.target.value)}
                dir="rtl"
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-dark-500 focus:border-dark-500 mb-2"
                rows="2"
                placeholder={t('blacklistModal.form.addNewNotePlaceholderAr')}
              />
              <div className="flex gap-2">
                <button type="button" onClick={handleAddNewNote} className="px-3 py-1 bg-dark-600 text-white text-sm rounded hover:bg-dark-700">
                  {t('blacklistModal.buttons.saveNote')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddNote(false)
                    setNewNote('')
                    setNewNoteAr('')
                  }}
                  className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400"
                >
                  {t('blacklistModal.buttons.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex justify-end">
        {!shouldDisableFields && (
          <button
            type="button"
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 sm:ml-3"
            onClick={handleSaveFraudUser}
          >
            {id ? t('blacklistModal.buttons.updateFraudUser') : t('blacklistModal.buttons.addFraudUser')}
          </button>
        )}
        <button
          type="button"
          className="ml-2 inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none"
          onClick={() => close()}
        >
          {t('blacklistModal.buttons.close')}
        </button>
      </div>
    </div>
  )
}

export default AddBlackList
